import type { CadEditor, Modifiers } from './editor/editor';
import { Vec2 } from '@lakaran/geometry';

const PAN_BUTTON = 1;

function modifiersOf(event: PointerEvent | MouseEvent): Modifiers {
  return { shift: event.shiftKey, ctrl: event.ctrlKey, alt: event.altKey };
}

/** Wires pointer, wheel and keyboard input on the render surface to the editor. */
export function attachInput(canvas: HTMLCanvasElement, editor: CadEditor): () => void {
  let panning = false;
  let panLast: Vec2 | null = null;
  let leftDown = false;

  const toLocal = (event: MouseEvent | PointerEvent | WheelEvent): Vec2 => {
    const rect = canvas.getBoundingClientRect();
    return Vec2.of(event.clientX - rect.left, event.clientY - rect.top);
  };

  const onPointerDown = (event: PointerEvent): void => {
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic events have no active pointer to capture.
    }
    editor.setAngleConstraint(event.ctrlKey);
    const point = toLocal(event);

    if (event.button === PAN_BUTTON || (event.button === 0 && event.altKey)) {
      panning = true;
      panLast = point;
      event.preventDefault();
      return;
    }
    if (event.button === 2) {
      editor.pointerDown(point, 'right', modifiersOf(event));
      return;
    }
    if (event.button === 0) {
      leftDown = true;
      editor.pointerDown(point, 'left', modifiersOf(event));
    }
  };

  const onPointerMove = (event: PointerEvent): void => {
    editor.setAngleConstraint(event.ctrlKey);
    const point = toLocal(event);
    if (panning && panLast) {
      editor.panBy(point.x - panLast.x, point.y - panLast.y);
      panLast = point;
      return;
    }
    editor.pointerMove(point);
  };

  const onPointerUp = (event: PointerEvent): void => {
    const point = toLocal(event);
    if (panning) {
      panning = false;
      panLast = null;
      return;
    }
    if (leftDown) {
      leftDown = false;
      editor.pointerUp(point, modifiersOf(event));
    }
  };

  const onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const point = toLocal(event);
    const factor = Math.exp(-event.deltaY * 0.0015);
    editor.zoomAt(point, factor);
  };

  const onContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  const onDoubleClick = (event: MouseEvent): void => {
    const point = toLocal(event);
    // A dimension opens its measurement field; everything else keeps its normal double-click.
    if (editor.editDimensionAt(point)) event.preventDefault();
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Control') {
      editor.setAngleConstraint(true);
      return;
    }
    if (event.key === 'F8') {
      editor.setGridVisible(!editor.settings.grid.visible);
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLElement | null;
    const typing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
    if (typing) return;
    // The node editor owns the keyboard while it has focus; its Del/Ctrl+C act on nodes.
    if (target instanceof HTMLElement && target.closest('.nodes-shell')) return;
    if (editor.keyDown(event)) event.preventDefault();
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    if (event.key === 'Control') editor.setAngleConstraint(false);
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('dblclick', onDoubleClick);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return () => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerUp);
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('contextmenu', onContextMenu);
    canvas.removeEventListener('dblclick', onDoubleClick);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  };
}
